import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useNavigate } from "react-router-dom";

import { useAppSelector, useAppDispatch } from "store/index";
import {
  getGamesList,
  handlePaginate,
  handleSelectAll,
  handleSelectItem,
  updateGamesBunch,
} from "store/gameCenter/games/slice";
import { gameCenterLimits } from "configs/limits";
import { ModalTypes } from "configs/types";
import { openModal } from "store/modals/slice";
import { NotificationTypes, showNotification } from "store/notifications/slice";

import ToggleBtn from "components/buttons/ToggleBtn";
import DefaultDropdown from "components/dropdowns/DefaultDropdown/DefaultDropdown";
import Pagination from "components/Pagination";
import GamesFilter from "./components/GamesFilter";

import editIcon from "assets/icons/edit-icon.svg";
import listIcon from "assets/icons/list-icon.svg";
import gridIcon from "assets/icons/grid-icon.svg";
import { Table } from "components/table/Table";
import EmptyState from "components/EmptyState/EmptyState";
import { useGuideModal } from "modals/guides/useGuideModal";
import { GuideButton } from "modals/guides/GuideButton";
import DataTableSkeleton from "components/global/Skeleton/TableSkeleton/DataTableSkeleton";
import ImageWithFallback from "helpers/ImageWithFallback";
import { getGameImage } from "helpers/getGameImage";
import PaginationSkeleton from "components/global/Skeleton/PaginationSkeleton/PaginationSkeleton";
import CopyToClipboard from "components/common/CopyToClipboard/CopyToClipboard";
import { usePermissionHelper } from "helpers/usePermissionHelper";
import { useRefreshList } from "helpers/useRefreshList";
import { RefreshButton } from "components/buttons/RefreshButton";
import { ActiveLabel } from "components/table/components/ActiveLabel/ActiveLabel";
import BooleanLabel from "components/common/BooleanLabel/BooleanLabel";

const isEnabledGameFlag = (value: unknown) =>
  ["true", "1"].includes(String(value).toLowerCase());

const GamesList: React.FC = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const { openGuide } = useGuideModal();
  const isDeactivatedList = location.pathname.includes("deactivated");
  const processingItemId = useAppSelector(
    (store) => store.games.processingItemId,
  );
  const tableScrollRef = useRef<HTMLDivElement>(null);
  const tableScrollTopRef = useRef(0);
  const shouldRestoreScrollRef = useRef(false);

  const [order, setOrder] = useState<{
    id: string;
    slug: string;
    title: string;
    providerTitle: string;
  }>({
    id: "none",
    slug: "none",
    title: "none",
    providerTitle: "none",
  });
  const [currentLimit, setLimit] = useState<number>(gameCenterLimits[2]);
  const [activeView, setView] = useState<string>("table");
  const [search, setSearch] = useState<string>("");
  const [type, setType] = useState("");
  const [freeSpins, setFreeSpins] = useState<
    { value: string | number; label: string }[]
  >([{ value: 2, label: t("all") }]);
  const [providers, setProviders] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [collections, setCollections] = useState<
    { value: string | number; label: string }[]
  >([]);
  const [tags, setTags] = useState<{ value: string | number; label: string }[]>(
    [],
  );
  const [geos, setGeos] = useState<{ value: string | number; label: string }[]>(
    [],
  );
  const [options, setOptions] = useState([
    {
      label: t("activate"),
      value: "activate",
    },
    {
      label: t("deactivate"),
      value: "deactivate",
    },
    {
      label: t("mobile"),
      value: "mobile",
    },
    {
      label: t("onFreeSpins"),
      value: "onFreeSpins",
    },
    {
      label: t("onDemoMode"),
      value: "onDemoMode",
    },
    {
      label: t("addToCollection"),
      value: "addToCollection",
    },
    {
      label: t("addToTag"),
      value: "addToTag",
    },
  ]);

  const gamesList = useAppSelector((store) => store.games.list);
  const isListFetched = useAppSelector((store) => store.games.isListFetched);
  const totalItems = useAppSelector((store) => store.games.totalItems);
  const totalPages = useAppSelector((store) => store.games.totalPages);
  const activePage = useAppSelector((store) => store.games.activePage);
  const selectedItems = useAppSelector((store) => store.games.selectedItems);
  const { refresh, isRefreshRequested } = useRefreshList({
    sliceName: "games",
    isListFetched,
  });

  const permissions = usePermissionHelper();
  const freeSpinsFilterValue = freeSpins[0]?.value;

  const getFilterString = () => {
    if (location.pathname.includes("active")) {
      return { isActive: 1 };
    }
    if (location.pathname.includes("deactivated")) {
      return { isDeactivated: 1 };
    }
    if (location.pathname.includes("no-category")) {
      return { hasCategory: 0 };
    }
    if (location.pathname.includes("hidden")) {
      return { isHidden: 1 };
    }
    if (location.pathname.includes("new")) {
      return { isNew: 1 };
    }
    return { isActive: 0 };
  };

  const setOptionsHandler = () => {
    if (location.pathname.includes("active")) {
      setOptions([
        {
          label: t("disable"),
          value: "disable",
        },
        {
          label: t("deactivate"),
          value: "deactivate",
        },
        {
          label: t("top"),
          value: "top",
        },
        {
          label: t("vip"),
          value: "vip",
        },
        {
          label: t("new").toUpperCase(),
          value: "new",
        },
        {
          label: t("popular"),
          value: "popular",
        },
        {
          label: t("exclusive"),
          value: "exclusive",
        },
        {
          label: t("trending"),
          value: "trending",
        },
        {
          label: t("mobile"),
          value: "mobile",
        },
        {
          label: t("onFreeSpins"),
          value: "onFreeSpins",
        },
        {
          label: t("onDemoMode"),
          value: "onDemoMode",
        },
        {
          label: t("addToCollection"),
          value: "addToCollection",
        },
        {
          label: t("addToTag"),
          value: "addToTag",
        },
        {
          label: t("addToTournament"),
          value: "addToTournament",
        },
      ]);
    }
    if (location.pathname.includes("deactivated")) {
      setOptions([
        {
          label: t("activate"),
          value: "activate",
        },
        {
          label: t("addToCollection"),
          value: "addToCollection",
        },
        {
          label: t("addToTag"),
          value: "addToTag",
        },
      ]);
    }
    if (location.pathname.includes("new")) {
      setOptions([
        {
          label: t("activate"),
          value: "activate",
        },
        {
          label: t("deactivate"),
          value: "deactivate",
        },
        {
          label: t("new").toUpperCase(),
          value: "new",
        },
        {
          label: t("popular"),
          value: "popular",
        },
        {
          label: t("exclusive"),
          value: "exclusive",
        },
        {
          label: t("trending"),
          value: "trending",
        },
        {
          label: t("mobile"),
          value: "mobile",
        },
        {
          label: t("addToCollection"),
          value: "addToCollection",
        },
        {
          label: t("addToTag"),
          value: "addToTag",
        },
      ]);
    }
  };

  useEffect(() => {
    dispatch(handlePaginate(1));

    setOptionsHandler();

    const grid = localStorage.getItem("grid");

    if (grid) {
      setView(grid);
    }
  }, [location.pathname]);

  useEffect(() => {
    if (!isListFetched) {
      dispatch(
        getGamesList({
          limit: currentLimit,
          offset: activePage - 1,
          providerIds: providers.map((el) => el.value as number),
          collectionIds: collections.map((el) => el.value as number),
          tagIds: tags.map((el) => el.value as number),
          geos: geos.map((el) => el.value as string),
          gameTitle: search === "" ? undefined : search,
          type: type.trim() || undefined,
          hasFreeSpins:
            freeSpinsFilterValue === 0 || freeSpinsFilterValue === 1
              ? freeSpinsFilterValue
              : undefined,
          order,
          ...getFilterString(),
        }),
      );
    }
  }, [isListFetched, activePage, currentLimit, order]);

  useEffect(() => {
    const activeModal = new URLSearchParams(location.search).get("activeModal");

    if (!isListFetched && activeModal === ModalTypes.editGame) {
      shouldRestoreScrollRef.current = true;
    }
  }, [isListFetched, location.search]);

  useLayoutEffect(() => {
    if (
      isListFetched &&
      shouldRestoreScrollRef.current &&
      tableScrollRef.current
    ) {
      tableScrollRef.current.scrollTop = tableScrollTopRef.current;
      shouldRestoreScrollRef.current = false;
    }
  }, [isListFetched]);

  const handleAllClick = () => {
    dispatch(handleSelectAll());
  };
  const handleItemClick = (id: number) => {
    dispatch(handleSelectItem(id));
  };
  const handlePrevClick = () => {
    if (activePage > 1) {
      dispatch(handlePaginate(activePage - 1));
    }
  };
  const handleNextClick = () => {
    if (activePage !== totalPages) {
      dispatch(handlePaginate(activePage + 1));
    }
  };
  const handleFilterSearch = (s: string) => {
    setSearch(s);
  };

  const handleActionClick = async (e: string) => {
    if (selectedItems.length > 0) {
      let result: any;
      const actionsThatReloadList = [
        "activate",
        "deactivate",
        "mobile",
        "onFreeSpins",
        "onDemoMode",
        "disable",
        "top",
        "vip",
      ];

      if (actionsThatReloadList.includes(e)) {
        shouldRestoreScrollRef.current = true;
      }

      if (e === "addToTournament") {
        dispatch(
          openModal({
            activeModal: ModalTypes.addGamesToTournament,
          }),
        );
      }
      if (e === "addToCollection") {
        dispatch(
          openModal({
            activeModal: ModalTypes.addGames,
            addGameModalType: "collection",
          }),
        );
      }
      if (e === "addToTag") {
        dispatch(
          openModal({
            activeModal: ModalTypes.addGames,
            addGameModalType: "tag",
          }),
        );
      }
      if (e === "activate") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isActive: 1,
            isDeactivated: 0,
          }),
        );
      }
      if (e === "deactivate") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isDeactivated: 1,
          }),
        );
      }
      if (e === "mobile") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isMobile: 1,
          }),
        );
      }
      if (e === "onFreeSpins") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            hasFreeSpins: 1,
          }),
        );
      }
      if (e === "onDemoMode") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            hasDemo: 1,
          }),
        );
      }
      if (e === "disable") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isActive: 0,
          }),
        );
      }
      if (e === "top") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isTop: 1,
          }),
        );
      }
      if (e === "vip") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isVip: 1,
          }),
        );
      }
      if (e === "new") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isNew: 1,
          }),
        );
      }
      if (e === "popular") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isPopular: 1,
          }),
        );
      }
      if (e === "exclusive") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isExclusive: 1,
          }),
        );
      }
      if (e === "trending") {
        result = await dispatch(
          updateGamesBunch({
            gameIds: selectedItems,
            isTrending: 1,
          }),
        );
      }
      if (
        e !== "addToCollection" &&
        e !== "addToTag" &&
        e !== "addToTournament"
      ) {
        if (result?.payload === true) {
          dispatch(
            showNotification({
              title: t("success"),
              message: t("actionSuccess"),
              type: NotificationTypes.success,
            }),
          );
        } else {
          dispatch(
            showNotification({
              title: t("error"),
              message: t("actionError"),
              type: NotificationTypes.error,
            }),
          );
        }
      }
    }
  };

  const handleOpenEditModal = (id: number) => {
    navigate(
      `${location.pathname}?activeModal=${ModalTypes.editGame}&entityId=${id}`,
    );
    dispatch(openModal({ activeModal: ModalTypes.editGame }));
  };

  const handleApply = () => {
    dispatch(handlePaginate(1));
  };

  const handleOrderClick = (key: keyof typeof order) => {
    const current = order[key];
    let next: "asc" | "desc" | "none";

    if (current === "none") next = "asc";
    else if (current === "asc") next = "desc";
    else next = "none";

    const newOrder = {
      [key]: next,
      ...Object.fromEntries(Object.entries(order).filter(([k]) => k !== key)),
    };
    // @ts-ignore
    setOrder(newOrder);
    dispatch(handlePaginate(1));
  };

  const actionProps = permissions?.update
    ? {
        onEdit: (id: number) => handleOpenEditModal(id),
        onView: undefined,
      }
    : {
        onEdit: undefined,
        onView: (id: number) => handleOpenEditModal(id),
      };

  return (
    <div className="page-list-wrapper page-list--with-filter">
      <GamesFilter
        searchValue={search}
        handleSearch={handleFilterSearch}
        selectedProviders={providers}
        handleUpdateProviders={(e) => setProviders(e)}
        selectedCollections={collections}
        handleUpdateCollections={(e) => setCollections(e)}
        selectedTags={tags}
        handleUpdateTags={(e) => setTags(e)}
        selectedGeos={geos}
        handleUpdateGeos={(e) => setGeos(e)}
        typeValue={type}
        handleType={setType}
        selectedFreeSpins={freeSpins}
        handleUpdateFreeSpins={setFreeSpins}
        handleApply={handleApply}
      />
      <div className="page-list--with-filter-content">
        <div className="page-list-header">
          <div className="page-list-header--box page-list-header--box--right">
            <RefreshButton
              position="right"
              disabled={isRefreshRequested}
              isLoading={isRefreshRequested}
              onClick={() => refresh()}
            />
          </div>
          <div className="page-list-header--box page-list-header--box-actions">
            <ToggleBtn
              handleClick={(v) => {
                localStorage.setItem("grid", v);
                setView(v);
              }}
              activeValue={activeView}
              disabled={gamesList.length === 0}
              icons={[
                {
                  url: listIcon,
                  value: "table",
                },
                {
                  url: gridIcon,
                  value: "grid",
                },
              ]}
            />
            <GuideButton onClick={() => openGuide("gamecentre")} />
            <DefaultDropdown
              className="default-dropdown--sentence-case"
              onSelect={(e) => {
                handleActionClick(e as string);
              }}
              placeholder={t("chooseAction")}
              options={options}
              isSmall
              disabled={selectedItems.length === 0 || !permissions?.update}
            />
          </div>
        </div>

        {!isListFetched && (
          <>
            <DataTableSkeleton columnsCount={8} rowsCount={12} />
            <PaginationSkeleton />
          </>
        )}
        {isListFetched && (
          <>
            <div
              ref={tableScrollRef}
              onScroll={(event) => {
                tableScrollTopRef.current = event.currentTarget.scrollTop;
              }}
              className={`game-table-wrap ${activeView === "table" ? "" : "grid"}`}
            >
              {gamesList.length === 0 && (
                <EmptyState message={t("noGamesFound")} />
              )}

              {gamesList.length > 0 && (
                <>
                  {activeView === "table" ? (
                    <Table
                      data={gamesList}
                      order={order}
                      idField="id"
                      selectedItems={selectedItems}
                      onAllSelect={handleAllClick}
                      onSelect={(id) => handleItemClick(id)}
                      onSort={(id) => {
                        if (!id) return;

                        handleOrderClick(
                          id === "provider" ? "providerTitle" : id,
                        );
                      }}
                      hasDelete={false}
                      hasEdit={permissions?.update}
                      hasView={permissions?.read && !permissions?.update}
                      hasOptions={permissions?.read || permissions?.update}
                      processingId={processingItemId}
                      customColumns={(row) => ({
                        image: (
                          <ImageWithFallback
                            src={getGameImage(row)}
                            alt={row.title}
                            style={{
                              objectFit: "cover",
                              objectPosition: "center",
                              borderRadius: "4px",
                            }}
                          />
                        ),
                        title: (
                          <div className="copyable-title-cell">
                            <CopyToClipboard text={row.title} />
                            <span>{row.title}</span>
                          </div>
                        ),
                        isActive: (
                          <ActiveLabel
                            isActive={Boolean(
                              row.isActive &&
                                !row.isDeactivated &&
                                !isDeactivatedList,
                            )}
                          />
                        ),
                        hasFreeSpins: (
                          <BooleanLabel
                            value={isEnabledGameFlag(row.hasFreeSpins)}
                          />
                        ),
                        hasDemo: (
                          <BooleanLabel
                            value={isEnabledGameFlag(row.hasDemo)}
                          />
                        ),
                      })}
                      columns={{
                        hiddenColumns: [
                          "images",
                          "isMobile",
                          "isHidden",
                          "isDeactivated",
                        ],
                        columnOrder: [
                          "id",
                          "image",
                          "title",
                          "slug",
                          "provider",
                          "hasDemo",
                          "hasFreeSpins",
                          "type",
                          "devices",
                          "isActive",
                        ],
                        sortingColumns: {
                          provider: {
                            value: t("provider"),
                            hasSorting: true,
                            sortedBy: order.providerTitle,
                          },
                        },
                      }}
                      {...actionProps}
                    />
                  ) : (
                    <div className="game-grid">
                      {gamesList.map((el) => (
                        <div className="game-item" key={el.id}>
                          <div className="game-item-img">
                            <ImageWithFallback
                              src={getGameImage(el)}
                              alt={el.title}
                              className="game-item-img__image"
                            />
                          </div>
                          <div className="game-item-content">
                            <div className="game-item-content--title">
                              {el.title}
                            </div>
                            <div className="game-item-content--actions">
                              <div
                                onClick={() => handleItemClick(el.id)}
                                className={`def-check-control ${
                                  selectedItems.includes(el.id) ? "checked" : ""
                                }`}
                              />
                              <div className="provider">
                                {el.provider.title}
                              </div>
                              <div
                                className="option"
                                onClick={() => {
                                  if (permissions?.update) {
                                    handleOpenEditModal(el.id);
                                  }
                                }}
                              >
                                <img src={editIcon} alt="" />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>

            {gamesList.length > 0 && (
              <Pagination
                handleLimitClick={(e) => {
                  setLimit(e);
                  dispatch(handlePaginate(1));
                }}
                limitsArray={gameCenterLimits}
                activePage={activePage}
                limit={currentLimit}
                totalItems={totalItems}
                totalPages={totalPages}
                handleNextClick={handleNextClick}
                handlePrevClick={handlePrevClick}
              />
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default GamesList;
